import * as React from 'react';
import * as ReactDOM from 'react-dom';
import AttachmentDataProviderType from 'owa-service/lib/contract/AttachmentDataProviderType';
import FileProvidersToAddListView, { FileProviderToAddInfo } from './FileProvidersToAddListView';
import getProviderBrandName from 'owa-fileprovider-store/lib/utils/getProviderBrandName';
import HeaderContainer from './HeaderContainer';
import StorageAccountsListView from './StorageAccountsListView';
import strings from 'owa-strings/owa-options-strings';
import { AddStorageAccountCallout } from './AddStorageAccountCallout';
import {
    FileProviderType,
    FileProviderViewState,
    ProviderLoadStateOption,
} from 'owa-fileprovider-store';
import { getAddableFileProviders } from 'owa-fileprovider-store/lib/selectors/getAddableFileProviders';
import { getFileProviders } from 'owa-fileprovider-store/lib/selectors/getFileProviders';
import { getProviderLoadState } from 'owa-fileprovider-store/lib/selectors/getProviderLoadState';
import { isProviderBeingRemoved } from 'owa-fileprovider-store/lib/selectors/isProviderBeingRemoved';
import { LightningId } from 'owa-lightning-v2';
import { observer } from 'mobx-react';
import { Spinner } from 'office-ui-fabric-react/lib/Spinner';
import {
    lazyAddFileProvider,
    lazyLoadProviders,
    lazyRemoveFileProvider,
} from 'owa-fileprovider-store';

export interface AddProviderState {
    isOpen: boolean;
}

const styles = require('./StorageAccountsFull.scss');

interface StorageAccountCategories {
    primaryAccounts: FileProviderViewState[];
    secondaryAccounts: FileProviderViewState[];
}

@observer
export default class StorageAccountsFull extends React.Component<{}, AddProviderState> {
    private addFileProviderAction: (providerType: FileProviderType) => void = null;

    constructor(props: {}) {
        super(props);
        this.state = {
            isOpen: true,
        };
    }
    componentDidMount() {
        // Initialize the addFileProvider to avoid popup blocker to trigger
        lazyAddFileProvider.import().then(addFileProvider => {
            this.addFileProviderAction = addFileProvider;
        });
        this.initializeStorageAccountsOption();
    }

    render() {
        let target: HTMLElement;
        const addedAccounts = getFileProviders();
        const { primaryAccounts, secondaryAccounts } = this.getStorageAccountCategories(
            addedAccounts
        );
        const providersToAddInfo = this.getStorageAccountToAdd(getAddableFileProviders());

        return getProviderLoadState() === ProviderLoadStateOption.Loaded ? (
            <div className={styles.container}>
                <div>
                    {providersToAddInfo.length !== 0 && (
                        <div>
                            <HeaderContainer
                                className={styles.fileProvidersToAddContainer}
                                ref={ref => (target = ReactDOM.findDOMNode(ref) as HTMLElement)}
                                header={strings.options_storageAccounts_addAccountHeader}>
                                <FileProvidersToAddListView
                                    providersToAddInfo={providersToAddInfo}
                                />
                            </HeaderContainer>
                            <AddStorageAccountCallout
                                lid={LightningId.AddStorageAccountCallout} // lightning id for callout
                                when={lightup => setTimeout(lightup, 500)}
                                target={() => target}
                            />
                        </div>
                    )}

                    <StorageAccountsListView
                        header={strings.options_storageAccounts_primaryAccount}
                        storageAccounts={primaryAccounts}
                    />
                    {secondaryAccounts.length !== 0 && (
                        <StorageAccountsListView
                            header={strings.options_storageAccounts_otherAccounts}
                            storageAccounts={secondaryAccounts}
                            onRemoveHandler={this.removeAccount}
                        />
                    )}
                </div>
            </div>
        ) : (
            <Spinner />
        );
    }

    private getStorageAccountToAdd(accountsToAdd: FileProviderType[]): FileProviderToAddInfo[] {
        return accountsToAdd.map(account => {
            return {
                providerType: account,
                providerName: getProviderBrandName(account),
                onClick: this.getAddStorageAccountFunc(account),
            };
        });
    }

    private initializeStorageAccountsOption = async () => {
        (await lazyLoadProviders.import())();
    };

    private getAddStorageAccountFunc = (providerType: FileProviderType) => {
        return () => {
            if (this.addFileProviderAction) {
                this.addFileProviderAction(providerType);
            } else {
                lazyAddFileProvider.import().then(addFileProviderAction => {
                    addFileProviderAction(providerType);
                });
            }
        };
    };

    private getStorageAccountCategories(
        providers: FileProviderViewState[]
    ): StorageAccountCategories {
        const [primaryProvider, ...secondaryProviders] = providers.filter(
            p =>
                p.type !== AttachmentDataProviderType.Mailbox &&
                !p.associatedWithPrimaryProvider &&
                p.type !== AttachmentDataProviderType.MailMessage
        );

        return {
            primaryAccounts: primaryProvider ? [primaryProvider] : [],
            secondaryAccounts: secondaryProviders,
        };
    }

    private removeAccount = (providerType: FileProviderType) => {
        if (!isProviderBeingRemoved(providerType)) {
            lazyRemoveFileProvider.import().then(removeFileProviderAction => {
                removeFileProviderAction(providerType);
            });
        }
    };
}
